import React, { useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Mail, Phone, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { hasGibberishWord, hasExcessiveRepeats } from '../utils/spamCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Site keys are public by design (they're embedded in the page HTML), so a
// hardcoded fallback here is safe — it just avoids requiring a CI env var.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LelWqMtAAAAAAitjh-tLvt3Bh6LnSIkvgp7GOi_';

const FIELD_LIMITS = {
  name: { min: 2, max: 100 },
  company: { max: 150 },
  subject: { min: 3, max: 150 },
  message: { min: 10, max: 5000 },
};

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', subject: '', message: '', website: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  // Recorded once when the form mounts; the backend uses it to spot bots
  // that submit implausibly fast after the page loads.
  const formRenderedAt = useRef(Date.now());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const name = formData.name.trim();
    const company = formData.company.trim();
    const subject = formData.subject.trim();
    const message = formData.message.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (name.length < FIELD_LIMITS.name.min || name.length > FIELD_LIMITS.name.max) {
      return `Name must be between ${FIELD_LIMITS.name.min} and ${FIELD_LIMITS.name.max} characters.`;
    }
    if (!emailRegex.test(formData.email.trim())) {
      return 'Please enter a valid email address.';
    }
    if (company.length > FIELD_LIMITS.company.max) {
      return `Company must be under ${FIELD_LIMITS.company.max} characters.`;
    }
    if (subject.length < FIELD_LIMITS.subject.min || subject.length > FIELD_LIMITS.subject.max) {
      return `Subject must be between ${FIELD_LIMITS.subject.min} and ${FIELD_LIMITS.subject.max} characters.`;
    }
    if (message.length < FIELD_LIMITS.message.min || message.length > FIELD_LIMITS.message.max) {
      return `Message must be between ${FIELD_LIMITS.message.min} and ${FIELD_LIMITS.message.max} characters.`;
    }
    if (hasGibberishWord(message) || hasExcessiveRepeats(message)) {
      return 'Your message looks like random or repeated characters. Please write an actual message.';
    }
    if (!recaptchaToken) {
      return 'Please verify you are not a robot.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setErrorMessage('');

    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus('error');
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/contact`, {
        ...formData,
        formRenderedAt: formRenderedAt.current,
        recaptchaToken,
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', subject: '', message: '', website: '' });
      formRenderedAt.current = Date.now();
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err.response?.data?.message || '');
    } finally {
      // reCAPTCHA tokens are single-use — reset regardless of outcome
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full border border-[#D64545] bg-white px-2.5 py-1.5 sm:px-4 sm:py-3 text-[12px] sm:text-[14px] text-[#0F0F0F] outline-none';

  const labelClass = 'block text-[11px] sm:text-[13px] font-semibold text-[#000000] mb-1 sm:mb-1.5';

  return (
    <div data-testid="contact-page" className="pt-[88px] lg:pt-[64px]">

      {/* ── Hero ── */}
      <section className="relative bg-[#D64545] text-white overflow-hidden">
        {/* White grid backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none select-none"
          style={{
            backgroundImage: 'linear-gradient(to right,rgba(255,255,255,0.12) 1px,transparent 1px),linear-gradient(to bottom,rgba(255,255,255,0.12) 1px,transparent 1px)',
            backgroundSize: '100px 100px',
          }}
        />
        <img
          src="/TakumiT.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{
            right: '1%',
            top: '-25%',
            width: '52%',
            height: 'auto',
            opacity: 0.31,
            filter: 'brightness(0) invert(1)',
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-24 lg:py-36">
          <h1
            className="font-display leading-[1.0] text-white mb-4"
            style={{ fontWeight: 600, fontSize: 'clamp(2.5rem, 14vw, 128px)' }}
          >
            {t('contact.title')}
          </h1>
          <p
            className="text-[18px] sm:text-[24px] lg:text-[28px] text-[#1E1E1E]"
            style={{ fontFamily: "'Barlow', sans-serif", fontWeight: 500, lineHeight: 1.4 }}
          >
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* ── Form + Contact info ── */}
      <section className="relative bg-[#EEEFF1] py-5 sm:py-10 lg:py-16">
        {/* Light grid backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none select-none"
          style={{
            backgroundImage: 'linear-gradient(to right,#D9D9D9 1px,transparent 1px),linear-gradient(to bottom,#D9D9D9 1px,transparent 1px)',
            backgroundSize: '100px 100px',
            opacity: 0.3,
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-start">

            {/* Left: Form */}
            <div>
              <h2 className="font-sans font-semibold text-[20px] sm:text-[36px] lg:text-[40px] text-[#D64545] mb-4 sm:mb-8">{t('contact.sendMessage')}</h2>

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-2.5 sm:space-y-5">
                {/* Honeypot — invisible to real users, bots that auto-fill every field trip it */}
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website" type="text" name="website" value={formData.website}
                    onChange={handleChange} tabIndex={-1} autoComplete="off"
                  />
                </div>

                {/* Row 1: Full Name + Email */}
                <div className="grid grid-cols-2 gap-2 sm:gap-5">
                  <div>
                    <label htmlFor="contact-name" className={labelClass}>{t('contact.form.name')}*</label>
                    <input
                      id="contact-name" type="text" name="name" value={formData.name}
                      onChange={handleChange} required autoComplete="name"
                      minLength={FIELD_LIMITS.name.min} maxLength={FIELD_LIMITS.name.max}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>{t('contact.form.email')}*</label>
                    <input
                      id="contact-email" type="email" name="email" value={formData.email}
                      onChange={handleChange} required autoComplete="email"
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Row 2: Company + Subject */}
                <div className="grid grid-cols-2 gap-2 sm:gap-5">
                  <div>
                    <label htmlFor="contact-company" className={labelClass}>{t('contact.form.company')}</label>
                    <input
                      id="contact-company" type="text" name="company" value={formData.company}
                      onChange={handleChange} autoComplete="organization"
                      maxLength={FIELD_LIMITS.company.max}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className={labelClass}>{t('contact.form.subject')}*</label>
                    <input
                      id="contact-subject" type="text" name="subject" value={formData.subject}
                      onChange={handleChange} required
                      minLength={FIELD_LIMITS.subject.min} maxLength={FIELD_LIMITS.subject.max}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Row 3: Message */}
                <div>
                  <label htmlFor="contact-message" className={labelClass}>{t('contact.form.message')}*</label>
                  <textarea
                    id="contact-message" name="message" value={formData.message}
                    onChange={handleChange} required rows={4}
                    minLength={FIELD_LIMITS.message.min} maxLength={FIELD_LIMITS.message.max}
                    className={`${inputClass} resize-none sm:min-h-[176px]`}
                  />
                </div>

                <div>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>

                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#D64545] text-[#EEEFF1] px-5 py-2 sm:px-8 sm:py-3.5 text-[13px] sm:text-[15px] font-semibold hover:bg-[#BC2929] active:bg-[#0042B8] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-2 border border-green-200 bg-green-50 px-4 py-3 text-green-700 text-[14px]">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span>{t('contact.successMsg')}</span>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 border border-red-100 bg-red-50 px-4 py-3 text-[#D64545] text-[14px]">
                    <XCircle className="h-4 w-4 shrink-0" />
                    <span>{errorMessage || t('contact.errorMsg')}</span>
                  </div>
                )}
              </form>
            </div>

            {/* Right: Contact info */}
            <div>
              <h2 className="font-sans font-semibold text-[28px] sm:text-[36px] lg:text-[40px] text-[#D64545] mb-8">{t('contact.contactInfo')}</h2>

              <div className="space-y-4">

                {/* India HQ */}
                <div className="bg-white rounded-xl border border-[#E5E6ED] overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E5E6ED]">
                    <span className="inline-flex w-6 h-4 overflow-hidden rounded-sm shrink-0 border border-[#E5E6ED]">
                      <img src="https://flagcdn.com/w40/in.png" alt="India flag" width="40" height="27" loading="lazy" className="w-full h-full object-cover" />
                    </span>
                    <span className="font-semibold text-[15px] text-[#0F0F0F]">{t('contact.indiaHead')}</span>
                  </div>
                  <div className="px-6 py-5 space-y-3 text-[14px] text-[#4A4A4A]">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-[#D64545] shrink-0 mt-0.5" />
                      <span className="leading-relaxed">
                        2nd Cross, Wheelers Road Ext, Da Costa Layout,<br />
                        St. Thomas Town, Bengaluru – 560084,<br />
                        Karnataka, India
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-[#D64545] shrink-0" />
                      <span>+91 91 8720 6241</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-[#D64545] shrink-0" />
                      <a href="mailto:info@takumi.co.in" className="hover:text-[#D64545] transition-colors">info@takumi.co.in</a>
                    </div>
                  </div>
                </div>

                {/* Japan HQ */}
                <div className="bg-white rounded-xl border border-[#E5E6ED] overflow-hidden">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E5E6ED]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#D64545] shrink-0" />
                    <span className="font-semibold text-[15px] text-[#0F0F0F]">{t('contact.japanHQ')}</span>
                  </div>
                  <div className="px-6 py-5 space-y-3 text-[14px] text-[#4A4A4A]">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-[#D64545] shrink-0 mt-0.5" />
                      <span className="leading-relaxed">
                        {t('footer.japanAddress').map((line, i, arr) => (
                          <React.Fragment key={i}>
                            {line}{i < arr.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-[#D64545] shrink-0" />
                      <a href="mailto:info@takumi.co.in" className="hover:text-[#D64545] transition-colors">info@takumi.co.in</a>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Map ── */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="border border-[#E5E6ED] overflow-hidden">
          <iframe
            key={language}
            title={language === 'jp' ? 'Takumi Motion Controls Kyoto Japan' : 'Takumi Motion Controls Bangalore'}
            src={language === 'jp'
              ? 'https://maps.google.com/maps?cid=18061947281251137999&ie=UTF8&iwloc=&output=embed'
              : 'https://maps.google.com/maps?q=Takumi%20Motion%20Controls%20Pvt%20Ltd,%20Da%20Costa%20Layout,%20Bangalore&t=&z=15&ie=UTF8&iwloc=&output=embed'
            }
            className="w-full h-[420px] border-0 block"
            loading="lazy"
          />
        </div>
      </div>

    </div>
  );
};

export default ContactPage;
